/**
 * Layout planning for the turn-in cards hand. Pure (no RN) so it can be
 * unit-tested. The hand normally renders as a single non-scrolling row, but a
 * large forced hand (>6 cards) in PORTRAIT would shrink each card to an
 * unreadable sliver — so portrait wraps onto 2 balanced rows. Landscape always
 * stays a single row (plenty of horizontal room).
 */

import { NAME_LINE_HEIGHT_RATIO } from './cardNameFit'

export interface CardRowPlan {
  /** Number of stacked rows (1 normally, 2 when a big portrait hand wraps). */
  rowCount: number
  /** Cards per row — drives the per-card width budget. */
  cardsPerRow: number
}

/** Portrait wraps to 2 rows once the hand exceeds this many cards. */
export const WRAP_THRESHOLD = 6

export function planCardRows(handLength: number, isLandscape: boolean): CardRowPlan {
  const wrap = !isLandscape && handLength > WRAP_THRESHOLD
  if (!wrap) return { rowCount: 1, cardsPerRow: handLength }
  // Split as evenly as possible, with the fuller row first (e.g. 7 → 4 + 3).
  return { rowCount: 2, cardsPerRow: Math.ceil(handLength / 2) }
}

/** Split a hand into the rows described by planCardRows (fuller row first). */
export function splitHandIntoRows<T>(hand: T[], isLandscape: boolean): T[][] {
  const { rowCount, cardsPerRow } = planCardRows(hand.length, isLandscape)
  if (rowCount === 1) return [hand]
  return [hand.slice(0, cardsPerRow), hand.slice(cardsPerRow)]
}

/** Share of the card's inner height given to the territory-name band. */
export const NAME_BAND_SHARE = 0.25

/**
 * Splits the card face into a fixed top zone (type, emoji, title) and a fixed
 * territory-name band, so every card in a hand keeps its art and title at the
 * same height no matter how long its territory name is. Scaled stencil text is
 * reserved before the decorative emoji gets the remaining space.
 */
export function cardFaceLayout(width: number, height: number, fontScale = 1) {
  const padding = width < 80 ? 4 : 8
  // Bungee-Regular.ttf advance widths: ARTILLERY = 6.226 em,
  // BLITZFIN = 5.317 em (the widest labels in their respective rows).
  // Budget the displayed font before native auto-fit; Android can otherwise
  // clip stencil glyphs in narrow cards with increased system font size.
  const innerWidth = Math.max(1, width - padding * 2 - 2)
  const scale = Math.max(1, fontScale)
  const typeLabelSize = Math.min(width < 80 ? 8 : width < 112 ? 9 : 10, innerWidth / (6.226 * scale))
  const accentLabelSize = Math.min(width < 80 ? 10 : 12, innerWidth / (5.317 * scale))
  const typeLineHeight = Math.ceil(typeLabelSize * 1.4)
  const accentLineHeight = Math.ceil(accentLabelSize * 1.4)
  // Territory names start a step above the old fixed size (the band reserves
  // the room) and may shrink to the floor before ellipsizing; see cardNameFit.
  const territoryBase = width < 80 ? 9 : 11
  const territoryMin = width < 80 ? 7 : 8
  const innerHeight = Math.max(0, height - padding * 2 - 4)
  // The band always holds two lines at the floor size, even on short cards.
  const nameBandHeight = Math.min(innerHeight, Math.max(
    Math.round(innerHeight * NAME_BAND_SHARE),
    2 * Math.ceil(territoryMin * fontScale * NAME_LINE_HEIGHT_RATIO),
  ))
  const topHeight = innerHeight - nameBandHeight
  const textHeight = Math.ceil(typeLineHeight * fontScale) + Math.ceil(accentLineHeight * fontScale) + 2
  const emojiSize = Math.max(0, Math.min(Math.round(width * 0.46), Math.floor(topHeight - textHeight)))
  // Horizontal room for the name, less 2pt each side so italic overhang isn't clipped.
  const nameWidth = Math.max(1, width - padding * 2 - 4 - 4)
  return { padding, typeLabelSize, accentLabelSize, typeLineHeight, accentLineHeight,
    territoryBase, territoryMin, nameBandHeight, topHeight, nameWidth, emojiSize }
}
